"use client";

import { useEffect, useRef } from "react";
import { ArrowRight } from "lucide-react";
import Link from "@/components/i18n/link";
import { useMessages } from "@/lib/i18n/client";
import { track } from "@/lib/tracking/track";
import { trackGamePromoClick, trackGamePromoView } from "@/lib/tracking/game-funnel";
import type { GameInviteSource } from "@/lib/tracking/events";
import { cn } from "@/lib/utils";
import messages from "./daily-word-invite.i18n";

export const DAILY_WORD_PATH = "/juegos/palabra-del-dia";

// Cuadritos decorativos (como un intento ya jugado), sin letras: no
// adelantan nada de la palabra.
const TILES = ["bg-emerald-600", "bg-amber-500", "bg-zinc-400 dark:bg-zinc-600", "bg-emerald-600", "bg-amber-500"];

function Tiles({ className }: { className?: string }) {
  return (
    <div aria-hidden className={cn("flex shrink-0 gap-1", className)}>
      {TILES.map((color, i) => (
        <span key={i} className={cn("rounded-[4px]", color)} />
      ))}
    </div>
  );
}

// Métricas internas: la invitación cuenta como vista cuando al menos la
// mitad aparece en pantalla (una vez por pestaña y lugar), no al cargar la
// página: así "la vieron" no incluye a quien nunca bajó hasta ella.
function useSeenOnce<T extends HTMLElement>(source: GameInviteSource) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        trackGamePromoView("daily-word", source);
        observer.disconnect();
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [source]);
  return ref;
}

// Invitación a "La palabra del día" desde otras páginas (home, explorador de
// eventos, Viva Cine): no todos entran por la home.
export function DailyWordInvite({
  source,
  variant = "compact",
  className,
}: {
  source: GameInviteSource;
  variant?: "banner" | "compact";
  className?: string;
}) {
  const t = useMessages(messages);
  // Uno por variante (solo una se monta).
  const bannerRef = useSeenOnce<HTMLDivElement>(source);
  const linkRef = useSeenOnce<HTMLAnchorElement>(source);
  const onClick = () => {
    track("game_invite_click", { game: "daily_word", source });
    trackGamePromoClick("daily-word", source);
  };

  if (variant === "banner") {
    return (
      <div
        ref={bannerRef}
        className={cn(
          "flex flex-col items-start gap-6 rounded-3xl border border-border bg-card p-8 md:flex-row md:items-center md:justify-between md:p-10",
          className,
        )}
      >
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-6">
          <Tiles className="[&>span]:h-10 [&>span]:w-10" />
          <div className="max-w-xl">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary">{t.eyebrow}</p>
            <h2 className="mt-1 text-2xl font-semibold tracking-tight md:text-3xl">{t.title}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{t.body}</p>
          </div>
        </div>
        <Link
          href={DAILY_WORD_PATH}
          onClick={onClick}
          className="inline-flex h-11 shrink-0 items-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
        >
          {t.play} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    );
  }

  return (
    <Link
      ref={linkRef}
      href={DAILY_WORD_PATH}
      onClick={onClick}
      className={cn(
        "group flex items-center gap-4 rounded-2xl border border-border bg-card p-4 transition hover:border-foreground/20 hover:shadow-soft",
        className,
      )}
    >
      <Tiles className="hidden sm:flex [&>span]:h-7 [&>span]:w-7" />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold uppercase tracking-widest text-primary">{t.eyebrow}</p>
        <p className="mt-0.5 font-semibold">{t.title}</p>
        <p className="text-sm text-muted-foreground">{t.short}</p>
      </div>
      <span className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-primary">
        <span className="hidden sm:inline">{t.play}</span>
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </span>
    </Link>
  );
}
